import type { IntlShape } from "@formatjs/intl";
import { Flex, Tag } from "antd";
import type { KeyboardEvent } from "react";
import { getFileTypeIcon } from "../utils/getFileTypeIcon";
import styles from "./CommentAttachments.module.css";
import {
  FilePreviewModal,
  type PreviewableFile,
  useFilePreview,
} from "./FilePreviewModal";

export type CommentFileRelation = {
  id?: string;
  file?: PreviewableFile | null;
};

type CommentAttachmentsProps = {
  fileRelations: CommentFileRelation[] | undefined;
  getDownloadUrl: (file: PreviewableFile) => string;
  intl: IntlShape;
};

export const CommentAttachments = ({
  fileRelations,
  getDownloadUrl,
  intl,
}: CommentAttachmentsProps) => {
  const { previewFile, openPreview, closePreview } = useFilePreview();

  const attachedFiles = (fileRelations ?? []).flatMap((relation) =>
    relation.file ? [{ key: relation.id ?? relation.file.id, file: relation.file }] : []
  );

  if (attachedFiles.length === 0) return null;

  const openPreviewOnKeyboardActivation = (
    event: KeyboardEvent<HTMLSpanElement>,
    file: PreviewableFile
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPreview(file);
    }
  };

  return (
    <>
      <Flex wrap gap={4} className={styles.attachmentList}>
        {attachedFiles.map(({ key, file }) => (
          <Tag
            key={key}
            variant="outlined"
            role="button"
            tabIndex={0}
            title={file.name ?? undefined}
            icon={getFileTypeIcon({ contentType: file.content_type, name: file.name })}
            className={styles.attachmentChip}
            onClick={() => openPreview(file)}
            onKeyDown={(event) => openPreviewOnKeyboardActivation(event, file)}
          >
            {file.name}
          </Tag>
        ))}
      </Flex>
      <FilePreviewModal
        file={previewFile}
        onClose={closePreview}
        getDownloadUrl={getDownloadUrl}
        intl={intl}
      />
    </>
  );
};
